import React from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { 
  Shield, 
  Eye, 
  Lock, 
  Database, 
  Users, 
  Globe,
  Calendar,
  AlertTriangle
} from 'lucide-react';

const PrivacyPolicyPage = () => {
  const lastUpdated = "December 1, 2024";

  const sections = [
    {
      title: "Information We Collect",
      icon: <Database className="h-6 w-6 text-primary" />,
      content: [
        {
          subtitle: "Personal Information",
          description: "We collect information you provide directly to us, such as when you create an account, including your name, email address, phone number, and profile information."
        },
        {
          subtitle: "Usage Information",
          description: "We automatically collect certain information about your use of our services, including your IP address, browser type, device information, and usage patterns."
        },
        {
          subtitle: "Cricket Data",
          description: "We collect cricket-related data including match scores, player statistics, tournament information, and team details that you input into our platform."
        }
      ]
    },
    {
      title: "How We Use Your Information",
      icon: <Eye className="h-6 w-6 text-primary" />,
      content: [
        {
          subtitle: "Service Provision",
          description: "To provide, maintain, and improve our cricket scoring services, including processing match data and generating statistics."
        },
        {
          subtitle: "Communication",
          description: "To communicate with you about your account, service updates, and respond to your inquiries and support requests."
        },
        {
          subtitle: "Analytics",
          description: "To analyze usage patterns and improve our platform's functionality and user experience."
        }
      ]
    },
    {
      title: "Information Sharing",
      icon: <Users className="h-6 w-6 text-primary" />,
      content: [
        {
          subtitle: "With Your Consent",
          description: "We may share your information with third parties when you explicitly consent to such sharing."
        },
        {
          subtitle: "Service Providers",
          description: "We may share information with trusted service providers who assist us in operating our platform and providing services."
        },
        {
          subtitle: "Legal Requirements",
          description: "We may disclose information if required by law or to protect our rights, property, or safety."
        }
      ]
    },
    {
      title: "Data Security",
      icon: <Lock className="h-6 w-6 text-primary" />,
      content: [
        {
          subtitle: "Encryption",
          description: "We use industry-standard encryption to protect your data both in transit and at rest."
        },
        {
          subtitle: "Access Controls",
          description: "We implement strict access controls and authentication measures to ensure only authorized personnel can access your data."
        },
        {
          subtitle: "Regular Audits",
          description: "We conduct regular security audits and assessments to maintain the highest level of data protection."
        }
      ]
    },
    {
      title: "Data Retention",
      icon: <Calendar className="h-6 w-6 text-primary" />,
      content: [
        {
          subtitle: "Account Data",
          description: "We retain your account information for as long as your account is active or as needed to provide services."
        },
        {
          subtitle: "Cricket Data",
          description: "Cricket match data and statistics are retained indefinitely unless you request deletion, as this data is valuable for historical records."
        },
        {
          subtitle: "Deletion Requests",
          description: "You may request deletion of your personal data, and we will process such requests in accordance with applicable law."
        }
      ]
    },
    {
      title: "Your Rights",
      icon: <Shield className="h-6 w-6 text-primary" />,
      content: [
        {
          subtitle: "Access",
          description: "You have the right to access the personal information we hold about you and request a copy of your data."
        },
        {
          subtitle: "Correction",
          description: "You can update or correct your personal information through your account settings or by contacting us."
        },
        {
          subtitle: "Deletion",
          description: "You may request deletion of your personal information, subject to certain legal and contractual obligations."
        }
      ]
    }
  ];

  const cookies = [
    {
      type: "Essential Cookies",
      description: "Required for basic platform functionality and security",
      duration: "Session"
    },
    {
      type: "Analytics Cookies",
      description: "Help us understand how users interact with our platform",
      duration: "2 years"
    },
    {
      type: "Preference Cookies",
      description: "Remember your settings and preferences",
      duration: "1 year"
    }
  ];

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="text-center mb-12">
          <h1 className="text-4xl font-bold text-foreground mb-4">
            Privacy Policy
          </h1>
          <p className="text-xl text-muted-foreground mb-4">
            Your privacy is important to us. This policy explains how we collect, use, and protect your information.
          </p>
          <div className="flex items-center justify-center gap-4">
            <Badge variant="outline">
              <Calendar className="h-3 w-3 mr-1" />
              Last Updated: {lastUpdated}
            </Badge>
            <Badge variant="secondary">
              <Shield className="h-3 w-3 mr-1" />
              GDPR Compliant
            </Badge>
          </div>
        </div>

        {/* Introduction */}
        <Card className="mb-8 bg-gradient-to-r from-primary/5 to-secondary/5 border-primary/20">
          <CardContent className="pt-6">
            <h2 className="text-2xl font-bold text-foreground mb-4">Introduction</h2>
            <p className="text-muted-foreground mb-4">
              Cricket Scoring ("we," "our," or "us") is committed to protecting your privacy. This Privacy Policy explains how we collect, use, and protect your information when you use our cricket scoring platform and related services.
            </p>
            <p className="text-muted-foreground">
              By using our services, you agree to the collection and use of information in accordance with this policy. If you do not agree with our policies and practices, please do not use our services.
            </p>
          </CardContent>
        </Card>

        {/* Main Sections */}
        <div className="space-y-8">
          {sections.map((section, index) => (
            <Card key={index} className="hover:shadow-lg transition-shadow">
              <CardHeader>
                <div className="flex items-center space-x-3">
                  {section.icon}
                  <CardTitle className="text-xl">{section.title}</CardTitle>
                </div>
              </CardHeader>
              <CardContent>
                <div className="space-y-6">
                  {section.content.map((item, itemIndex) => (
                    <div key={itemIndex} className="space-y-2">
                      <h3 className="font-semibold text-foreground">{item.subtitle}</h3>
                      <p className="text-muted-foreground">{item.description}</p>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Cookies Section */}
        <Card className="mt-8">
          <CardHeader>
            <CardTitle className="text-xl flex items-center space-x-2">
              <Globe className="h-5 w-5 text-primary" />
              <span>Cookies and Tracking Technologies</span>
            </CardTitle>
            <CardDescription>
              We use cookies and similar tracking technologies to enhance your experience on our platform.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {cookies.map((cookie, index) => (
                <div key={index} className="flex items-start justify-between p-4 border rounded-lg">
                  <div>
                    <h3 className="font-semibold text-foreground">{cookie.type}</h3>
                    <p className="text-sm text-muted-foreground">{cookie.description}</p>
                  </div>
                  <Badge variant="outline">{cookie.duration}</Badge>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* International Transfers */}
        <Card className="mt-8">
          <CardHeader>
            <CardTitle className="text-xl">International Data Transfers</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-muted-foreground mb-4">
              Your information may be transferred to and processed in countries other than your own. We ensure that such transfers comply with applicable data protection laws and implement appropriate safeguards.
            </p>
            <div className="flex items-center space-x-2 text-sm text-muted-foreground">
              <AlertTriangle className="h-4 w-4" />
              <span>We use standard contractual clauses and other approved mechanisms for international data transfers.</span>
            </div>
          </CardContent>
        </Card>

        {/* Children's Privacy */}
        <Card className="mt-8">
          <CardHeader>
            <CardTitle className="text-xl">Children's Privacy</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-muted-foreground">
              Our services are not intended for children under 13 years of age. We do not knowingly collect personal information from children under 13. If you are a parent or guardian and believe your child has provided us with personal information, please contact us immediately.
            </p>
          </CardContent>
        </Card>

        {/* Changes to Policy */}
        <Card className="mt-8">
          <CardHeader>
            <CardTitle className="text-xl">Changes to This Privacy Policy</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-muted-foreground mb-4">
              We may update this Privacy Policy from time to time. We will notify you of any changes by posting the new Privacy Policy on this page and updating the "Last Updated" date.
            </p>
            <p className="text-muted-foreground">
              We encourage you to review this Privacy Policy periodically for any changes. Your continued use of our services after any changes constitutes acceptance of the updated policy.
            </p>
          </CardContent>
        </Card>

        {/* Contact Information */}
        <Card className="mt-8 bg-gradient-to-r from-primary/10 to-secondary/10 border-primary/20">
          <CardContent className="pt-6 text-center">
            <h2 className="text-xl font-bold text-foreground mb-4">Contact Us</h2>
            <p className="text-muted-foreground mb-4">
              If you have any questions about this Privacy Policy or our data practices, please contact us:
            </p>
            <div className="space-y-2 text-sm">
              <p className="text-foreground">
                <strong>Email:</strong> privacy@cricketscoring.com
              </p>
              <p className="text-foreground">
                <strong>Address:</strong> 123 Cricket Lane, Sports City, SC 12345
              </p>
              <p className="text-foreground">
                <strong>Phone:</strong> +1 (555) 123-4567
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default PrivacyPolicyPage; 